import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Download, Loader2, FileText, CheckCircle2, AlertCircle,
  Building2, User, Lightbulb, ExternalLink,
  Link as LinkIcon, RefreshCw, Phone, GitBranch, Plus, ChevronRight, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getScoreColor(score) {
  if (score >= 70) return 'text-green-600';
  if (score >= 40) return 'text-yellow-600';
  return 'text-red-600';
}

function getScoreBgClass(score) {
  if (score >= 70) return 'bg-green-500';
  if (score >= 40) return 'bg-yellow-500';
  return 'bg-red-500';
}

function getRiskBadge(risk) {
  switch (risk?.toLowerCase()) {
    case 'low':
      return <Badge className="bg-green-100 text-green-700 border-green-200">Low Risk</Badge>;
    case 'high':
      return <Badge className="bg-red-100 text-red-700 border-red-200">High Risk</Badge>;
    default:
      return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Medium Risk</Badge>;
  }
}

export default function CallSummary() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [summary, setSummary] = useState(null);
  const [framework, setFramework] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [crmDialogOpen, setCrmDialogOpen] = useState(false);
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [crmUrl, setCrmUrl] = useState('');
  const [crmType, setCrmType] = useState('salesforce');
  const [savingCrm, setSavingCrm] = useState(false);
  const [pushingCrm, setPushingCrm] = useState(false);
  const [crmConnections, setCrmConnections] = useState([]);
  const [selectedFollowUpFramework, setSelectedFollowUpFramework] = useState('follow-up');
  const [companySettings, setCompanySettings] = useState(null);

  useEffect(() => {
    fetchSession();
    fetchFrameworks();
    fetchCompanySettings();
    fetchCrmConnections();
  }, [sessionId]);

  const fetchCompanySettings = async () => {
    try {
      const { data } = await axios.get(`${API}/company/settings`);
      setCompanySettings(data);
    } catch (e) {
      console.error('Failed to fetch company settings:', e);
    }
  };

  const fetchCrmConnections = async () => {
    try {
      const { data } = await axios.get(`${API}/crm/connections`);
      setCrmConnections(data);
    } catch (e) {
      console.error('Failed to fetch CRM connections:', e);
    }
  };

  const pushToCrm = async () => {
    setPushingCrm(true);
    try {
      const { data } = await axios.post(
        `${API}/crm/push`,
        { session_id: sessionId },
        
      );
      toast.success(data.message || 'Summary pushed to CRM');
      setSession(prev => ({ ...prev, crm_pushed: true, crm_push_provider: data.provider }));
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to push to CRM';
      toast.error(detail);
    } finally {
      setPushingCrm(false);
    }
  };

  const primaryCallLabel = companySettings?.call_type_labels?.primary_call || 'Qualification Call';
  const followUpCallLabel = companySettings?.call_type_labels?.follow_up_call || 'Follow-Up Qualification';

  const fetchFrameworks = async () => {
    try {
      const { data } = await axios.get(`${API}/frameworks`);
      setFrameworks(data);
    } catch (e) {
      console.error('Failed to fetch frameworks:', e);
    }
  };

  const fetchSession = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sessions/${sessionId}`);
      setSession(data);
      
      if (data.post_call_summary) {
        setSummary(data.post_call_summary);
      }
      
      // Fetch framework
      if (data.framework_id) {
        try {
          const fwRes = await axios.get(`${API}/frameworks/${data.framework_id}`);
          setFramework(fwRes.data);
        } catch (e) {
          console.error('Failed to fetch framework:', e);
        }
      }
      
      if (data.crm_link) {
        setCrmUrl(data.crm_link);
      }
      
      // Fetch follow-up calls
      try {
        const followUpRes = await axios.get(`${API}/sessions/${sessionId}/follow-ups`);
        setFollowUps(followUpRes.data.follow_ups || []);
      } catch (e) {
        // No follow-ups or not parent session
        setFollowUps([]);
      }
    } catch (e) {
      console.error('Failed to fetch session:', e);
      toast.error('Failed to load call details');
      navigate('/history');
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const { data } = await axios.post(
        `${API}/sessions/${sessionId}/generate-summary`,
        {},
        
      );
      setSummary(data);
      toast.success('Summary generated successfully');
    } catch (e) {
      console.error('Failed to generate summary:', e);
      toast.error('Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(
        `${API}/sessions/${sessionId}/export-pdf`,
        { 
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `call_summary_${session?.prospect_name || 'unknown'}_${session?.created_at?.slice(0, 10) || 'date'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (e) {
      console.error('Failed to export PDF:', e);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const saveCrmLink = async () => {
    if (!crmUrl.trim()) {
      toast.error('Please enter a CRM URL');
      return;
    }
    
    setSavingCrm(true);
    try {
      await axios.post(
        `${API}/sessions/${sessionId}/crm-link`,
        { crm_url: crmUrl, crm_type: crmType },
        
      );
      setSession(prev => ({ ...prev, crm_link: crmUrl }));
      setCrmDialogOpen(false);
      toast.success('CRM link saved');
    } catch (e) {
      console.error('Failed to save CRM link:', e);
      toast.error('Failed to save CRM link');
    } finally {
      setSavingCrm(false);
    }
  };

  const startFollowUpCall = () => {
    // Navigate to dashboard with follow-up params
    const parentId = session?.parent_session_id || sessionId;
    navigate(`/?follow_up=${parentId}&framework=${selectedFollowUpFramework}&prospect=${encodeURIComponent(session?.prospect_name || '')}&company=${encodeURIComponent(session?.company_name || '')}`);
  };

  const getOverallScore = () => {
    const scores = session?.qualification_scores || {};
    if (Object.keys(scores).length === 0) return 0;
    return Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / Object.keys(scores).length);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Loading call details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="call-summary-page">
      {/* Header */}
      <header className="h-16 border-b border-zinc-200 flex items-center justify-between px-6 bg-white">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/history')} className="rounded-md" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to History
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {/* Follow-up Call Button */}
          <Dialog open={followUpDialogOpen} onOpenChange={setFollowUpDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-md bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100" data-testid="follow-up-btn">
                <GitBranch className="w-4 h-4 mr-2" />
                {followUpCallLabel}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Start {followUpCallLabel}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <p className="text-sm text-zinc-600">
                  Continue qualifying this prospect. Previous scores and context will carry forward to help you close remaining gaps.
                </p>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Framework</label>
                  <Select value={selectedFollowUpFramework} onValueChange={setSelectedFollowUpFramework}>
                    <SelectTrigger data-testid="follow-up-framework-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {frameworks.map(fw => (
                        <SelectItem key={fw.id} value={fw.id}>
                          {fw.name}
                          {fw.id === 'follow-up' && (
                            <span className="text-xs text-zinc-500 ml-2">(recommended)</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={startFollowUpCall}
                  className="w-full rounded-md bg-purple-600 hover:bg-purple-700"
                  data-testid="start-follow-up-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Start {followUpCallLabel}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Push to CRM Button */}
          {crmConnections.length > 0 && summary && (
            <Button
              variant="outline"
              onClick={pushToCrm}
              disabled={pushingCrm}
              className={`rounded-md ${session?.crm_pushed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'}`}
              data-testid="push-crm-btn"
            >
              {pushingCrm ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : session?.crm_pushed ? (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {session?.crm_pushed ? 'Pushed to CRM' : 'Push to CRM'}
            </Button>
          )}

          {/* CRM Link Dialog */}
          <Dialog open={crmDialogOpen} onOpenChange={setCrmDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-md" data-testid="link-crm-btn">
                <LinkIcon className="w-4 h-4 mr-2" />
                {session?.crm_link ? 'Update CRM' : 'Link to CRM'}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Link to CRM</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">CRM Platform</label>
                  <Select value={crmType} onValueChange={setCrmType}>
                    <SelectTrigger data-testid="crm-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salesforce">Salesforce</SelectItem>
                      <SelectItem value="hubspot">HubSpot</SelectItem>
                      <SelectItem value="pipedrive">Pipedrive</SelectItem>
                      <SelectItem value="zoho">Zoho CRM</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">CRM Record URL</label>
                  <Input
                    placeholder="https://your-crm.com/opportunity/12345"
                    value={crmUrl}
                    onChange={(e) => setCrmUrl(e.target.value)}
                    data-testid="crm-url-input"
                  />
                  <p className="text-xs text-zinc-500">
                    Paste the direct link to this opportunity/lead in your CRM
                  </p>
                </div>
                <Button 
                  onClick={saveCrmLink} 
                  disabled={savingCrm}
                  className="w-full rounded-md bg-brand-primary hover:bg-brand-hover"
                  data-testid="save-crm-btn"
                >
                  {savingCrm ? 'Saving...' : 'Save CRM Link'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button 
            variant="outline" 
            onClick={exportPdf}
            disabled={exportingPdf}
            className="rounded-md"
            data-testid="export-pdf-btn"
          >
            {exportingPdf ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Parent Session Link (if this is a follow-up) */}
        {session?.parent_session && (
          <div className="bg-purple-50 border border-purple-200 rounded-md p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GitBranch className="w-4 h-4 text-purple-600" />
              <span className="text-sm text-purple-800">
                {followUpCallLabel} #{session.call_number} for {session.parent_session.prospect_name}
              </span>
            </div>
            <Link 
              to={`/call/${session.parent_session.id}`}
              className="text-sm text-purple-700 hover:underline flex items-center gap-1"
            >
              View original call
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Call Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-zinc-200 shadow-none rounded-md">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>
                      {session?.prospect_name || 'Unknown Prospect'}
                    </CardTitle>
                    {session?.call_number > 1 && (
                      <Badge className="bg-purple-100 text-purple-700">
                        Call #{session.call_number}
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="flex items-center gap-2 mt-1">
                    {session?.company_name && (
                      <>
                        <Building2 className="w-4 h-4" />
                        {session.company_name}
                      </>
                    )}
                    {session?.company_website && (
                      <a 
                        href={session.company_website.startsWith('http') ? session.company_website : `https://${session.company_website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-primary hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Website
                      </a>
                    )}
                    {session?.user_name && (
                      <span className="flex items-center gap-1 text-zinc-500">
                        <User className="w-3 h-3" />
                        {session.user_name}
                      </span>
                    )}
                  </CardDescription>
                </div>
                <Badge variant="outline" className={session?.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}>
                  {session?.status === 'completed' ? 'Completed' : 'In Progress'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-zinc-500 block">Date</span>
                  <span className="font-medium">{formatDate(session?.created_at)}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Framework</span>
                  <span className="font-medium">{framework?.name || session?.framework_id}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Messages</span>
                  <span className="font-medium">{session?.transcript?.length || 0}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Notes</span>
                  <span className="font-medium">{session?.solution_notes?.length || 0}</span>
                </div>
              </div>
              
              {session?.crm_link && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-800">Linked to CRM ({session.crm_type || 'CRM'})</span>
                  </div>
                  <a 
                    href={session.crm_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-700 hover:underline flex items-center gap-1"
                  >
                    Open in CRM
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Score Card */}
          <Card className="border-zinc-200 shadow-none rounded-md">
            <CardHeader>
              <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">
                Qualification Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-4">
                <span className={`text-5xl font-bold ${getScoreColor(getOverallScore())}`} style={{ fontFamily: 'var(--font-mono)' }}>
                  {getOverallScore()}%
                </span>
              </div>
              <Progress value={getOverallScore()} className="h-2 rounded-none mb-4" />
              
              <div className="space-y-2">
                {Object.entries(session?.qualification_scores || {}).map(([cat, score]) => (
                  <div key={cat} className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">{cat}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-zinc-100 rounded-none h-1.5 overflow-hidden">
                        <div 
                          className={`h-full ${getScoreBgClass(score)} transition-all`}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                      <span className={`text-sm font-mono ${getScoreColor(score)}`}>{score}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Follow-up Calls Chain */}
        {(followUps.length > 0 || session?.follow_up_count > 0) && (
          <Card className="border-zinc-200 shadow-none rounded-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                  <GitBranch className="w-4 h-4" />
                  Call Chain
                </CardTitle>
                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                  {followUps.length + 1} calls
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 overflow-x-auto pb-2">
                {/* Current/Parent call */}
                <div className="flex-shrink-0">
                  <div className={`p-3 border rounded-md min-w-[140px] ${!session?.parent_session_id ? 'border-blue-300 bg-brand-muted' : 'border-zinc-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Phone className="w-4 h-4 text-brand-primary" />
                      <span className="text-sm font-medium">Call #1</span>
                    </div>
                    <p className="text-xs text-zinc-500">{formatShortDate(session?.created_at)}</p>
                    <p className={`text-sm font-mono ${getScoreColor(getOverallScore())}`}>{getOverallScore()}%</p>
                  </div>
                </div>

                {followUps.map((fu, index) => (
                  <div key={fu.id} className="flex items-center gap-4 flex-shrink-0">
                    <ChevronRight className="w-4 h-4 text-zinc-300" />
                    <Link 
                      to={`/call/${fu.id}`}
                      className="p-3 border border-zinc-200 rounded-md min-w-[140px] hover:border-purple-300 hover:bg-purple-50 transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <GitBranch className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium">Call #{fu.call_number}</span>
                      </div>
                      <p className="text-xs text-zinc-500">{formatShortDate(fu.created_at)}</p>
                      <p className={`text-sm font-mono ${getScoreColor(
                        fu.qualification_scores ? 
                        Math.round(Object.values(fu.qualification_scores).reduce((a,b) => a+b, 0) / Object.keys(fu.qualification_scores).length) : 0
                      )}`}>
                        {fu.qualification_scores ? 
                          Math.round(Object.values(fu.qualification_scores).reduce((a,b) => a+b, 0) / Object.keys(fu.qualification_scores).length) : 0}%
                      </p>
                    </Link>
                  </div>
                ))}

                {/* Add follow-up button */}
                <div className="flex items-center gap-4 flex-shrink-0">
                  <ChevronRight className="w-4 h-4 text-zinc-300" />
                  <Button
                    variant="outline"
                    className="min-w-[140px] h-auto py-3 border-dashed border-purple-300 text-purple-600 hover:bg-purple-50"
                    onClick={() => setFollowUpDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add {followUpCallLabel}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Section */}
        <Card className="border-zinc-200 shadow-none rounded-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle style={{ fontFamily: 'var(--font-heading)' }}>
                Post-Call Summary
              </CardTitle>
              <Button 
                onClick={generateSummary}
                disabled={generatingSummary}
                variant={summary ? "outline" : "default"}
                className="rounded-md"
                data-testid="generate-summary-btn"
              >
                {generatingSummary ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {summary ? 'Regenerate' : 'Generate Summary'}
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!summary ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-zinc-900 mb-2">No summary yet</h3>
                <p className="text-zinc-500 mb-4">Generate an AI-powered summary of this call</p>
              </div>
            ) : (
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="w-full justify-start border-b border-zinc-200 rounded-none bg-transparent">
                  <TabsTrigger value="overview" className="data-[state=active]:border-b-2 data-[state=active]:border-brand-primary" data-testid="summary-overview-tab">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="qualification" className="data-[state=active]:border-b-2 data-[state=active]:border-brand-primary" data-testid="summary-qualification-tab">
                    Qualification
                  </TabsTrigger>
                  <TabsTrigger value="next-steps" className="data-[state=active]:border-b-2 data-[state=active]:border-brand-primary" data-testid="summary-next-steps-tab">
                    Next Steps
                  </TabsTrigger>
                  <TabsTrigger value="requirements" className="data-[state=active]:border-b-2 data-[state=active]:border-brand-primary" data-testid="summary-requirements-tab">
                    Requirements
                  </TabsTrigger>
                  <TabsTrigger value="solution-notes" className="data-[state=active]:border-b-2 data-[state=active]:border-brand-primary" data-testid="summary-solution-notes-tab">
                    Solution Notes
                  </TabsTrigger>
                  <TabsTrigger value="risk" className="data-[state=active]:border-b-2 data-[state=active]:border-brand-primary" data-testid="summary-risk-tab">
                    Risk Assessment
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="pt-4">
                  {summary.executive_summary && (
                    <div className="mb-6">
                      <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                        Executive Summary
                      </h4>
                      <p className="text-zinc-700 bg-brand-muted border border-brand-accent-muted rounded-md p-4">
                        {summary.executive_summary}
                      </p>
                    </div>
                  )}

                  {summary.prospect_profile && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {summary.prospect_profile.pain_points?.length > 0 && (
                        <div className="bg-zinc-50 rounded-md p-4">
                          <h5 className="font-medium text-zinc-900 mb-2 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            Pain Points
                          </h5>
                          <ul className="space-y-1">
                            {summary.prospect_profile.pain_points.map((p, i) => (
                              <li key={i} className="text-sm text-zinc-600">• {p}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {summary.prospect_profile.decision_makers?.length > 0 && (
                        <div className="bg-zinc-50 rounded-md p-4">
                          <h5 className="font-medium text-zinc-900 mb-2 flex items-center gap-2">
                            <User className="w-4 h-4 text-brand-primary" />
                            Decision Makers
                          </h5>
                          <ul className="space-y-1">
                            {summary.prospect_profile.decision_makers.map((d, i) => (
                              <li key={i} className="text-sm text-zinc-600">• {d}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {summary.prospect_profile.budget_indicators && (
                        <div className="bg-zinc-50 rounded-md p-4">
                          <h5 className="font-medium text-zinc-900 mb-2 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-green-500" />
                            Budget Indicators
                          </h5>
                          <p className="text-sm text-zinc-600">{summary.prospect_profile.budget_indicators}</p>
                        </div>
                      )}

                      {summary.prospect_profile.timeline_indicators && (
                        <div className="bg-zinc-50 rounded-md p-4">
                          <h5 className="font-medium text-zinc-900 mb-2 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-purple-500" />
                            Timeline Indicators
                          </h5>
                          <p className="text-sm text-zinc-600">{summary.prospect_profile.timeline_indicators}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {summary.follow_up_questions?.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                        Follow-up Questions
                      </h4>
                      <div className="space-y-2">
                        {summary.follow_up_questions.map((q, i) => (
                          <div key={i} className="bg-white border border-zinc-200 rounded-md p-3 flex items-start gap-2">
                            <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-zinc-700">{q}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="qualification" className="pt-4">
                  {summary.qualification_summary ? (
                    <div className="space-y-6">
                      {summary.qualification_summary.strengths?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                            Strengths
                          </h4>
                          <div className="space-y-2">
                            {summary.qualification_summary.strengths.map((s, i) => (
                              <div key={i} className="bg-green-50 border border-green-100 rounded-md p-3 flex items-start gap-2" data-testid={`strength-${i}`}>
                                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-green-900">{s}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {summary.qualification_summary.gaps?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                            Gaps to Address
                          </h4>
                          <div className="space-y-2">
                            {summary.qualification_summary.gaps.map((g, i) => (
                              <div key={i} className="bg-yellow-50 border border-yellow-100 rounded-md p-3 flex items-start gap-2" data-testid={`gap-${i}`}>
                                <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-yellow-900">{g}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {summary.qualification_summary.category_scores && Object.keys(summary.qualification_summary.category_scores).length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                            AI Category Scores
                          </h4>
                          <div className="space-y-2">
                            {Object.entries(summary.qualification_summary.category_scores).map(([cat, score]) => (
                              <div key={cat} className="flex items-center justify-between bg-white border border-zinc-200 rounded-md p-3">
                                <span className="text-sm font-medium text-zinc-700">{cat}</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 bg-zinc-100 rounded h-2 overflow-hidden">
                                    <div 
                                      className={`h-full ${getScoreBgClass(score)} transition-all`}
                                      style={{ width: `${score}%` }}
                                    />
                                  </div>
                                  <span className={`text-sm font-mono ${getScoreColor(score)}`}>{score}%</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-center py-8">No qualification analysis available</p>
                  )}
                </TabsContent>

                <TabsContent value="next-steps" className="pt-4">
                  {summary.next_steps?.length > 0 ? (
                    <div className="space-y-3">
                      {summary.next_steps.map((step, i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-md p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-6 h-6 rounded-full bg-brand-accent-muted flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-brand-primary">{i + 1}</span>
                              </div>
                              <div>
                                <p className="font-medium text-zinc-900">{step.action}</p>
                                {step.deadline && (
                                  <p className="text-sm text-zinc-500 mt-1">{step.deadline}</p>
                                )}
                              </div>
                            </div>
                            {step.owner && (
                              <Badge variant="outline" className="text-xs">
                                {step.owner}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-center py-8">No next steps identified</p>
                  )}
                </TabsContent>

                <TabsContent value="requirements" className="pt-4">
                  {summary.solution_requirements?.length > 0 ? (
                    <div className="space-y-2">
                      {summary.solution_requirements.map((req, i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-md p-3 flex items-start gap-3">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              req.priority === 'high' ? 'bg-red-50 text-red-700' :
                              req.priority === 'medium' ? 'bg-yellow-50 text-yellow-700' :
                              'bg-zinc-50 text-zinc-700'
                            }`}
                          >
                            {req.priority?.toUpperCase() || 'MEDIUM'}
                          </Badge>
                          <div>
                            <p className="text-sm text-zinc-900">{req.requirement}</p>
                            <Badge variant="outline" className="text-xs mt-1">{req.type}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-center py-8">No requirements captured</p>
                  )}

                  {summary.recommended_demo_focus?.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                        Recommended Demo Focus
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {summary.recommended_demo_focus.map((item, i) => (
                          <Badge key={i} className="bg-brand-muted text-brand-primary border-brand-accent">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>


                <TabsContent value="solution-notes" className="pt-4">
                  {session?.solution_notes?.length > 0 ? (
                    <div className="space-y-3">
                      {session.solution_notes.map((note, i) => (
                        <div key={note.id || i} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`summary-note-${i}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs bg-zinc-50">
                              {note.category || 'General'}
                            </Badge>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                note.requirement_type === 'technical' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                note.requirement_type === 'business' ? 'bg-green-50 text-green-700 border-green-200' :
                                'bg-brand-muted text-brand-primary border-brand-accent'
                              }`}
                            >
                              {note.requirement_type || 'functional'}
                            </Badge>
                          </div>
                          <p className="text-sm text-zinc-700">{note.note}</p>
                          {note.timestamp && (
                            <span className="text-xs text-zinc-400 mt-2 block">
                              {new Date(note.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                      <p className="text-zinc-500">No solution notes were captured during this call</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="risk" className="pt-4">
                  {summary.deal_risk_assessment ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-zinc-500">Risk Level:</span>
                        {getRiskBadge(summary.deal_risk_assessment.risk_level)}
                      </div>

                      {summary.deal_risk_assessment.risk_factors?.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-zinc-900 mb-2">Risk Factors</h5>
                          <ul className="space-y-1">
                            {summary.deal_risk_assessment.risk_factors.map((r, i) => (
                              <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                {r}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {summary.deal_risk_assessment.mitigation_strategies?.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-zinc-900 mb-2">Mitigation Strategies</h5>
                          <ul className="space-y-1">
                            {summary.deal_risk_assessment.mitigation_strategies.map((s, i) => (
                              <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-center py-8">No risk assessment available</p>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>

        {/* Transcript */}
        {session?.transcript?.length > 0 && (
          <Card className="border-zinc-200 shadow-none rounded-md">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'var(--font-heading)' }}>
                Call Transcript
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {session.transcript.map((entry, i) => (
                    <div key={i} className={`flex gap-3`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        entry.speaker === 'rep' ? 'bg-brand-accent-muted text-brand-primary' : 'bg-zinc-100 text-zinc-600'
                      }`}>
                        <User className="w-4 h-4" />
                      </div>
                      <div className={`flex-1 p-3 rounded-md ${
                        entry.speaker === 'rep' ? 'bg-zinc-100' : 'bg-white border border-zinc-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                            {entry.speaker === 'rep' ? 'You' : 'Prospect'}
                          </span>
                        </div>
                        <p className="text-zinc-900 text-sm">{entry.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
